import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

// With refetchOnMount/refetchOnWindowFocus off globally, a query that ends in
// error never retries by itself — one transient blip (API deploy restart,
// brief connection drop) leaves it dead until a full page reload. This hook
// quietly refetches the currently-errored active queries a few times with
// backoff.
//
// Used in two places:
// - ErrorView (the original #566 heal): retries while the error screen shows.
// - Screens that keep already-loaded content on a failed BACKGROUND refetch
//   (design editor): pass `enabled = hasData && isError` so the same ladder
//   heals the stale-but-visible query without ever tearing down the UI.
const RETRY_DELAYS_MS = [3_000, 10_000, 30_000];

export function useErroredQueriesHeal(enabled: boolean = true): void {
  const queryClient = useQueryClient();
  const attemptRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    // Each activation is a fresh error episode: restart the ladder. For the
    // always-enabled ErrorView case this runs once per mount, exactly like
    // the previous inline implementation.
    attemptRef.current = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;

    function scheduleNext() {
      if (stopped) return;
      const delay = RETRY_DELAYS_MS[attemptRef.current];
      if (delay === undefined) return;
      timer = setTimeout(() => {
        attemptRef.current += 1;
        void queryClient
          .refetchQueries({
            type: "active",
            predicate: (query) => query.state.status === "error",
          })
          .finally(scheduleNext);
      }, delay);
    }

    scheduleNext();
    return () => {
      stopped = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [queryClient, enabled]);
}
